import React, { useEffect, useState, useRef } from 'react';
import PersonalDetails from './steps/PersonalDetails';
import GDPRConsent from './steps/GDPRConsent/GDPRConsent';
import SelectService from './steps/SelectService';
import { recordServiceSelectionSafely } from 'src/config/consent/serviceSelection';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Shield, User, Stethoscope, ChevronLeft, ArrowRight, Sun, Moon, Monitor, AlertTriangle } from 'lucide-react';
import logoImage from "src/Images/Logo/SymbianLogo.png";
import HeaderUserbox from '../../layouts/SidebarLayout/Header/Userbox';
import RegionSelector from '../../layouts/SidebarLayout/Header/RegionSelector';
import { useThemeMode } from '../../theme/ThemeProvider';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from 'src/store/store';
import { fetchPatientProfile, setOnboardingCompleted } from 'src/store/reducers/authReducer';
import apiService from 'src/shared/Services/apiService';
import { PAYMENT_FALLBACK_ENABLED } from 'src/config/paymentFallback';
import { toast } from 'react-toastify';
import { useCountryConfiguration } from '../../hooks/useCountryConfiguration';
import { useRegion } from '../../contexts/RegionContext/RegionContext';

type ThemeMode = 'light' | 'dark' | 'system';
const themeCycle: ThemeMode[] = ['light', 'dark', 'system'];

const themeConfig: Record<ThemeMode, { icon: typeof Sun; label: string }> = {
  light: { icon: Sun, label: 'Light mode' },
  dark: { icon: Moon, label: 'Dark mode' },
  system: { icon: Monitor, label: 'System mode' },
};

type FormData = {
  personalDetails: Record<string, any>;
  selectedService: {
    value: string; label: string; price: string; price_id: string;
  } | null;
};

const steps = [
  {
    label: 'Data Consent',
    shortLabel: 'Consent',
    icon: Shield,
    num: '01',
    description: 'Review and accept the data protection policy that applies to your country.',
  },
  {
    label: 'Personal Details',
    shortLabel: 'Details',
    icon: User,
    num: '02',
    description: 'Complete your profile information.',
  },
  {
    label: 'Select Service',
    shortLabel: 'Service',
    icon: Stethoscope,
    num: '03',
    description: 'Choose your medical second opinion.',
  },
];

const StepperComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const onboardingCompleted = useSelector((state: RootState) => state.auth.onboardingCompleted);
  const hasPaidBefore = useSelector((state: RootState) => state.auth.hasPaidBefore);
  const authPk = useSelector((state: RootState) => state.auth.pk);
  // A patient who finished the steps but abandoned checkout comes back straight
  // to the service step rather than re-consenting and re-typing everything.
  const [activeStep, setActiveStep] = useState(onboardingCompleted ? 2 : 0);
  const [formData, setFormData] = useState<FormData>({ personalDetails: {}, selectedService: null });
  const [submitFunctions, setSubmitFunctions] = useState<Record<number, () => void>>({});
  const [loading, setLoading] = useState(false);
  const [isServiceSelected, setIsServiceSelected] = useState(false);
  // Set when Stripe checkout cannot be created — see paymentFallback.ts.
  const [paymentUnavailable, setPaymentUnavailable] = useState(false);
  const [open, setOpen] = useState(true);
  const locked = useRef(true);
  const [gdprReady, setGdprReady] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const { mode, setMode, isDark } = useThemeMode();

  const {
    country: detectedCountry,
    isoCode: detectedIso,
    countryConfiguration,
    detectionSource,
  } = useCountryConfiguration();

  const { isIndia } = useRegion();

  const editableCountry = isIndia
    ? (countryConfiguration?.editableCountry ?? true)
    : true;
  const lockPhoneCountry = isIndia
    ? detectedIso === 'IN'
    : false;

  useEffect(() => {
    if (detectedCountry && !selectedCountry) {
      setSelectedCountry(detectedCountry);
    }
  }, [detectedCountry]);

  const cycleTheme = () => {
    const currentIndex = themeCycle.indexOf(mode);
    const nextIndex = (currentIndex + 1) % themeCycle.length;
    setMode(themeCycle[nextIndex]);
  };
  const { icon: ThemeIcon, label: themeLabel } = themeConfig[mode];

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase())))
        e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  /* ── Force re-open if locked ── */
  useEffect(() => {
    const interval = setInterval(() => {
      if (locked.current && !open) setOpen(true);
    }, 500);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    const ctx = (e: MouseEvent) => e.preventDefault();
    const kbd = (e: KeyboardEvent) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase())))
        e.preventDefault();
    };
    document.addEventListener('contextmenu', ctx);
    document.addEventListener('keydown', kbd);
    return () => { document.removeEventListener('contextmenu', ctx); document.removeEventListener('keydown', kbd); };
  }, []);

  useEffect(() => {
    const id = setInterval(() => { if (locked.current && !open) setOpen(true); }, 500);
    return () => clearInterval(id);
  }, [open]);

  useEffect(() => {
    // `/patient-details` is deliberately NOT here: navigating to the profile by
    // hand used to close onboarding and skip payment entirely.
    if (location.pathname === '/payment-success' || location.pathname.startsWith('/services/')) {
      locked.current = false;
      setOpen(false);
    }

    // App.tsx owns the real gate (payment on the server); this only mirrors it.
    if (hasPaidBefore) {
      locked.current = false;
      setOpen(false);
    }
  }, [location.pathname, hasPaidBefore]);

  // The payment can complete while this tab sits on the Select Service step — in
  // another tab, or on Stripe's return page, which is configured server-side for the
  // deployed domain. Re-read the profile whenever the patient comes back, so a
  // confirmed payment opens the app (App.tsx's gate) without a reload.
  const lastProfileRefresh = useRef(0);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastProfileRefresh.current < 5000) return;
      lastProfileRefresh.current = Date.now();
      dispatch(fetchPatientProfile());
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [dispatch]);

  const handleNext = async () => {
    if (loading) return;
    if (activeStep === 0 && submitFunctions[0]) { submitFunctions[0](); return; }
    if (activeStep === 1 && submitFunctions[1]) { submitFunctions[1](); return; }
    if (activeStep === 2) {
      const pk = formData.personalDetails?.PK || authPk || '';
      const priceId = formData.selectedService?.price_id;

      // The steps themselves are finished either way.
      const markOnboardingDone = () => {
        dispatch(setOnboardingCompleted(true));
        if (pk) {
          apiService
            .updatePatientProfile({ PK: pk, onboarding_completed: true })
            .catch(() => console.warn('[onboarding] could not persist completion.'));
        }
      };

      // Fallback path: checkout is unavailable, so the patient continues into the
      // app instead of being stranded at onboarding.
      const continueWithoutPayment = () => {
        markOnboardingDone();
        locked.current = false; setOpen(false);
        navigate(`/patient-details/PersonalInformation?patientId=${pk}`);
      };

      if (PAYMENT_FALLBACK_ENABLED && paymentUnavailable) { continueWithoutPayment(); return; }

      if (!priceId) {
        if (PAYMENT_FALLBACK_ENABLED) {
          setPaymentUnavailable(true);
          toast.info('Payment is temporarily unavailable — you can continue and pay later.');
          return;
        }
        toast.error('This service is not available for payment right now. Please contact support.');
        return;
      }

      setLoading(true);
      try {
        markOnboardingDone();
        const res: any = await apiService.createStripePayment([{ price_id: priceId }]);
        const checkoutUrl = res?.url || res?.data?.url || res?.data?.data?.url;
        if (!checkoutUrl) throw new Error('payment response carried no checkout url');
        window.location.href = checkoutUrl;
      } catch (error: any) {
        console.error('[payment] could not create checkout session', error);
        setLoading(false);
        if (PAYMENT_FALLBACK_ENABLED) {
          setPaymentUnavailable(true);
          toast.info('Payment is temporarily unavailable — you can continue now and pay later.');
          return;
        }
        toast.error(error?.response?.data?.error || 'Payment could not be started. Please try again.');
      }
    }
  };

  const handleBack = () => setActiveStep((p) => p - 1);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <GDPRConsent
            onComplete={() => setActiveStep(1)}
            triggerSubmit={(fn) => setSubmitFunctions((prev) => ({ ...prev, [step]: fn }))}
            onStatusChange={(ready) => setGdprReady(ready)}
            onCountryChange={(c) => setSelectedCountry(c)}
            initialCountry={selectedCountry}
            editableCountry={editableCountry}
            editableConsent={countryConfiguration?.editableConsent ?? true}
            detectionSource={detectionSource}
          />
        );
      case 1:
        return (
          <PersonalDetails
            initialData={formData.personalDetails}
            onComplete={(data) => {
              setFormData((prev) => ({ ...prev, personalDetails: data.personalDetails }));
              setActiveStep(2);
            }}
            triggerSubmit={(fn) => setSubmitFunctions((prev) => ({ ...prev, [step]: fn }))}
            setLoading={setLoading}
            selectedCountry={selectedCountry}
            lockPhoneCountry={lockPhoneCountry}
          />
        );
      case 2:
        return (
          <SelectService
            onComplete={(data) => {
              setFormData((prev) => ({ ...prev, selectedService: data }));
              setIsServiceSelected(true);
              // Record the choice BEFORE Stripe, so an abandoned checkout still
              // leaves a trail. Deliberately not awaited and never throws — the
              // patient must not be blocked from continuing by an audit write.
              recordServiceSelectionSafely({
                serviceCode: data.value,
                serviceLabel: data.label,
                priceId: data.price_id,
                displayPrice: data.price,
              });
            }}
          />
        );
      default: return null;
    }
  };

  const currentStep = steps[activeStep];
  const CurrentStepIcon = currentStep.icon;
  const progressPercent = ((activeStep + 1) / steps.length) * 100;

  if (!open) return null;
  return (
    <div>
      {open && (
        <div className={`fixed inset-0 z-[9999] flex flex-col ${isDark ? 'bg-[#060C18]' : 'bg-[#F4F6FA]'}`}>

          <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
            <div className="absolute -top-[30%] -right-[20%] w-[55vw] h-[55vw] rounded-full bg-brand-500/[0.025] dark:bg-brand-400/[0.018] blur-[140px]" />
            <div className="absolute -bottom-[28%] -left-[15%] w-[45vw] h-[45vw] rounded-full bg-brand-600/[0.018] dark:bg-brand-300/[0.012] blur-[120px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vh] rounded-full bg-brand-400/[0.008] dark:bg-brand-500/[0.006] blur-[180px]" />
          </div>
          <div className={`relative z-50 flex-shrink-0 ${isDark ? 'bg-[#0A1220]/98 border-white/[0.07]' : 'bg-white/96 border-slate-900/[0.08]'} border-b backdrop-blur-xl shadow-[0_1px_0_0_rgba(0,0,0,0.05),0_8px_32px_-16px_rgba(3,45,105,0.18)]`}>
            <div className="relative z-[1450] flex h-[56px] items-center justify-between gap-2 sm:gap-4 px-3 sm:px-6 lg:px-8 xl:px-12">

              <div className="flex min-w-0 flex-shrink items-center gap-2 sm:gap-4">
                <img
                  src={isDark ? logoImage : "/assets/symbian-logo.svg"}
                  alt="Symbian Health"
                  className={`h-[22px] sm:h-[24px] w-auto flex-shrink-0`}
                />

                <div className={`hidden h-5 w-px lg:block ${isDark ? 'bg-white/[0.12]' : 'bg-slate-900/[0.10]'}`} />

                <div className="hidden min-w-0 items-center gap-3 lg:flex">
                  <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${isDark ? 'bg-white/[0.08] ring-1 ring-white/[0.10]' : 'bg-brand-50 ring-1 ring-brand-200/60'}`}>
                    <CurrentStepIcon size={14} className={isDark ? 'text-brand-300' : 'text-brand-600'} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                        Step {activeStep + 1} of {steps.length}
                      </span>
                    </div>
                    <p className={`text-[12.5px] font-bold truncate leading-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>
                      {currentStep.label}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`flex flex-shrink-0 items-center rounded-full px-1 sm:px-1.5 py-1 ${isDark ? 'bg-white/[0.05] ring-1 ring-white/[0.08]' : 'bg-slate-100/80 ring-1 ring-slate-200/80'}`}>
                {steps.map((step, index) => {
                  const isCompleted = index < activeStep;
                  const isActive = index === activeStep;
                  const StepIcon = step.icon;
                  return (
                    <React.Fragment key={step.label}>
                      {index > 0 && (
                        <div className={`w-4 sm:w-7 h-px rounded-full mx-0.5 transition-all duration-500 ${index <= activeStep
                          ? isDark ? 'bg-brand-400/50' : 'bg-brand-400/60'
                          : isDark ? 'bg-white/[0.10]' : 'bg-slate-300/60'
                          }`} />
                      )}
                      <div className={[
                        'flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
                        isActive
                          ? isDark
                            ? 'bg-brand-500/20 text-brand-300 ring-1 ring-brand-500/30'
                            : 'bg-white text-brand-700 shadow-sm ring-1 ring-brand-200/80'
                          : isCompleted
                            ? isDark ? 'text-brand-400' : 'text-brand-500'
                            : isDark ? 'text-white/30' : 'text-slate-400',
                      ].join(' ')}>
                        {isCompleted ? (
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isDark ? 'bg-brand-500/30' : 'bg-brand-100'}`}>
                            <Check size={9} strokeWidth={3} className={isDark ? 'text-brand-300' : 'text-brand-600'} />
                          </div>
                        ) : (
                          <StepIcon size={12} className="shrink-0" />
                        )}
                        <span className="hidden sm:inline">{step.shortLabel}</span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Right: Theme + Region flag + User */}
              <div className="relative z-[1450] flex min-w-0 flex-shrink items-center justify-end gap-0.5 sm:gap-1">
                <button
                  onClick={cycleTheme}
                  className={`p-2 rounded-lg transition-all duration-200 min-h-[34px] min-w-[34px] flex items-center justify-center ${isDark ? 'text-white/50 hover:text-white hover:bg-white/[0.08]' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`}
                  aria-label={themeLabel}
                  title={themeLabel}
                >
                  <ThemeIcon size={16} strokeWidth={1.8} />
                </button>
                <div className={`w-px h-[18px] mx-0.5 ${isDark ? 'bg-white/[0.09]' : 'bg-slate-900/[0.10]'}`} />
                <RegionSelector />
                <HeaderUserbox isMinimal />
              </div>
            </div>

            {/*
              Mobile step context. The header pills collapse to bare icons below
              `sm`, and the "Step N of M" block is `lg`-only, so without this a
              phone shows three anonymous dots and no idea which step this is.
            */}
            <div className={`lg:hidden flex items-center gap-2 px-3 sm:px-4 pb-2 -mt-0.5 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              <div className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md ${isDark ? 'bg-white/[0.08] ring-1 ring-white/[0.10]' : 'bg-brand-50 ring-1 ring-brand-200/60'}`}>
                <CurrentStepIcon size={12} className={isDark ? 'text-brand-300' : 'text-brand-600'} />
              </div>
              <p className="text-[13px] font-bold leading-tight truncate">{currentStep.label}</p>
              <span className={`ml-auto flex-shrink-0 text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                {activeStep + 1}/{steps.length}
              </span>
            </div>

            {/* Progress bar */}
            <div className={`h-[2px] ${isDark ? 'bg-white/[0.05]' : 'bg-slate-200/60'}`}>
              <motion.div
                className={`h-full relative ${isDark ? 'bg-gradient-to-r from-brand-600 via-brand-400 to-brand-300' : 'bg-gradient-to-r from-brand-700 via-brand-500 to-brand-400'}`}
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className={`absolute right-0 top-[-1px] w-6 h-[4px] blur-[3px] rounded-full ${isDark ? 'bg-brand-300/60' : 'bg-brand-400/80'}`} />
              </motion.div>
            </div>
          </div>

          <div className="relative z-10 flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
            <div className={`min-h-full flex flex-col ${isDark ? 'bg-[#060C18]' : 'bg-[#F4F6FA]'}`}>
              <div className="mx-auto w-full max-w-[1120px] px-4 sm:px-5 lg:px-6 py-5 sm:py-6 flex-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {renderStepContent(activeStep)}

                    {paymentUnavailable && (
                      <div className={`mt-4 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 ${isDark
                        ? 'border-amber-500/25 bg-amber-500/[0.08]'
                        : 'border-amber-200 bg-amber-50'}`}>
                        <AlertTriangle size={15} className="mt-[1px] shrink-0 text-amber-500" />
                        <p className="text-[13px] leading-snug text-amber-700 dark:text-amber-300">
                          Online payment is temporarily unavailable. Your details are saved — continue now
                          and our team will arrange the payment with you.
                        </p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* min-h + safe-area padding so the nav clears the iOS home indicator. */}
          <div
            className={`relative z-20 flex min-h-[58px] flex-shrink-0 items-center justify-between gap-3 overflow-visible border-t px-4 sm:px-6 lg:px-8 ${isDark
              ? 'bg-[#0A1220]/98 border-white/[0.07] backdrop-blur-xl'
              : 'bg-white/96 backdrop-blur-xl border-slate-900/[0.08] shadow-[0_-8px_32px_-16px_rgba(15,23,42,0.12)]'
              }`}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <button
              disabled={activeStep === 0}
              onClick={handleBack}
              className={`flex items-center gap-2 text-sm font-semibold disabled:opacity-0 disabled:pointer-events-none transition-all duration-200 px-4 py-2 rounded-xl border min-h-[38px] ${isDark
                ? 'text-white/60 hover:text-white border-white/[0.10] hover:border-white/[0.20] hover:bg-white/[0.06]'
                : 'text-slate-500 hover:text-slate-800 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
            >
              <ChevronLeft size={15} />
              Back
            </button>

            {(() => {
              const isNextDisabled = loading || (activeStep === 0 && !gdprReady) || (activeStep === 2 && !isServiceSelected);
              const tooltipText = loading
                ? 'Please wait — saving your data…'
                : activeStep === 0
                  ? 'Accept all required consents above to continue'
                  : activeStep === 2
                    ? 'Select a service above to continue'
                    : 'Complete all required fields to continue';

              return (
                <div className="relative group/cta">
                  {isNextDisabled && (
                    <div
                      role="tooltip"
                      className={[
                        'absolute bottom-[calc(100%+10px)] right-0 z-[9999]',
                        'whitespace-nowrap max-w-[280px] rounded-xl px-3 py-2',
                        'bg-slate-900 text-white text-xs font-medium leading-snug',
                        'dark:bg-slate-800 dark:ring-1 dark:ring-white/10',
                        'shadow-[0_8px_24px_-4px_rgba(0,0,0,0.45)]',
                        'opacity-0 translate-y-1 pointer-events-none',
                        'transition-all duration-200 ease-out',
                        'group-hover/cta:opacity-100 group-hover/cta:translate-y-0',
                      ].join(' ')}
                    >
                      {tooltipText}
                      <span
                        className="absolute -bottom-[5px] right-5 w-[10px] h-[10px] bg-slate-900 dark:bg-slate-800 rotate-45 rounded-sm"
                        aria-hidden="true"
                      />
                    </div>
                  )}

                  <button
                    onClick={handleNext}
                    disabled={isNextDisabled}
                    aria-disabled={isNextDisabled ? 'true' : 'false'}
                    className={[
                      'flex items-center gap-2 text-sm font-semibold text-white px-6 py-2.5 rounded-xl',
                      'transition-all duration-200 min-h-[38px]',
                      'disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-none disabled:bg-brand-400',
                      'enabled:active:scale-[0.97]',
                      isDark
                        ? 'bg-gradient-to-r from-brand-600 to-brand-500 shadow-[0_4px_16px_-4px_rgba(3,93,184,0.50)] enabled:hover:shadow-[0_8px_24px_-6px_rgba(3,93,184,0.65)] enabled:hover:from-brand-500 enabled:hover:to-brand-400'
                        : 'bg-gradient-to-r from-brand-700 to-brand-600 shadow-[0_4px_14px_-4px_rgba(3,93,184,0.40)] enabled:hover:shadow-[0_8px_24px_-6px_rgba(3,93,184,0.55)] enabled:hover:from-brand-600 enabled:hover:to-brand-500',
                    ].join(' ')}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      <>
                        {activeStep === 2 ? (paymentUnavailable ? 'Complete Profile' : 'Proceed to Payment') : 'Continue'}
                        {activeStep === 2
                          ? <Check size={14} className="transition-transform duration-200 enabled:group-hover:scale-110" />
                          : <ArrowRight size={14} className="transition-transform duration-200 enabled:group-hover:translate-x-0.5" />
                        }
                      </>
                    )}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepperComponent;
